import React from 'react';
import { Link } from 'react-router-dom';
import ColourTextLogo from '../../../../colour_text_logo.svg';
import './PrivacyPolicyScreen.css';

const PrivacyPolicyScreen = () => {
  return (
    <div className="legal-screen">
      <div className="legal-container">
        <div className="legal-gradient-header"></div>
        <div className="legal-card-content">
          <img src={ColourTextLogo} alt="Framework" className="legal-logo" />

          <h1 className="legal-title">Privacy Policy</h1>
          <p className="legal-last-updated">Last updated: 13 February 2026</p>

          <div className="legal-content">
            <section>
              <h2>1. Introduction</h2>
              <p>
                This Privacy Policy describes how Framework AI ("we", "us", or "our") collects, uses, stores, and shares your personal information when you use our services, including:
              </p>
              <ul>
                <li>Our <strong>Enterprise Prompt Library</strong> web service at frameworkai.com.au</li>
                <li>Our <strong>FrameworkAI Chrome Extension</strong>, which connects your Framework AI prompts with ChatGPT</li>
              </ul>
              <p>
                We are committed to protecting your privacy and ensuring transparency about our data practices. If your Product handles personal or sensitive user data, we post this privacy policy as required by the Chrome Web Store and applicable privacy laws.
              </p>
            </section>

            <section>
              <h2>2. User Data Collection</h2>
              <p>We collect the following information:</p>

              <h3>From the Enterprise Prompt Library (Web Service)</h3>
              <ul>
                <li><strong>Email address:</strong> The email address you provide when you sign up for an account, used for account management, authentication, and communication</li>
                <li><strong>Product usage:</strong> Information about how you use our Enterprise Prompt Library service, including which prompts you access, create, or interact with</li>
              </ul>

              <h3>From the FrameworkAI Chrome Extension</h3>
              <ul>
                <li><strong>Authentication credentials:</strong> When you log in through the extension, we collect your email address and password solely for authentication with the Framework AI API. Your password is never stored locally—only an authentication token (JWT) is stored</li>
                <li><strong>Authentication token:</strong> A JWT token stored locally in your browser (via Chrome's storage API) to maintain your session</li>
                <li><strong>User information:</strong> User ID, organization ID, job role, and related account metadata obtained from the Framework AI API or from your JWT token</li>
                <li><strong>Prompts data:</strong> Your saved prompts, prompt text, titles, descriptions, categories, and metadata—both from the Framework AI API and prompts you save locally or create within the extension</li>
                <li><strong>Settings and preferences:</strong> Extension settings such as preferred role, API base URL, sync interval, theme, and notification preferences</li>
                <li><strong>Session data:</strong> When you visit frameworkai.com.au while the extension is installed, the extension may read your existing session (auth token and user data from that site's storage) to sync your login state—this data stays on your device and is only used to authenticate the extension with our API</li>
              </ul>

              <h3>Information We Do NOT Collect</h3>
              <ul>
                <li>IP addresses</li>
                <li>Geographic location data</li>
                <li>Device information or browser metadata beyond what Chrome provides</li>
                <li>Cookies or tracking technologies beyond what is necessary for authentication</li>
                <li>Web browsing activity unrelated to our services</li>
                <li>Any data from websites other than frameworkai.com.au and the ChatGPT pages where the extension injects prompts</li>
              </ul>
            </section>

            <section>
              <h2>3. User Data Handling</h2>
              <p>We handle your data in the following ways:</p>

              <h3>Processing</h3>
              <ul>
                <li><strong>Authentication:</strong> Your email and password are transmitted securely (HTTPS) to our API for login. We verify your credentials and return an authentication token. Your password is never stored</li>
                <li><strong>Token validation:</strong> We periodically validate your authentication token with our API to ensure your session remains active</li>
                <li><strong>Prompt synchronization:</strong> When you are logged in, we fetch your prompts from the Framework AI API, cache them locally for faster access and offline use, and sync any new prompts you save from the extension back to our servers</li>
                <li><strong>Session bridging:</strong> If you are logged in on frameworkai.com.au, the extension may read your session data from that page to automatically authenticate the extension—no additional login required</li>
              </ul>

              <h3>Use of Information</h3>
              <p>We use the information we collect to:</p>
              <ul>
                <li>Authenticate your account and manage your access to Framework AI services</li>
                <li>Provide and maintain our Enterprise Prompt Library and Chrome extension</li>
                <li>Fetch, display, and inject your prompts into ChatGPT when you choose to use them</li>
                <li>Cache prompts locally for offline access and faster loading</li>
                <li>Remember your preferences and settings</li>
                <li>Communicate with you about your account and our services (where applicable)</li>
                <li>Improve our service based on usage patterns (in aggregate, where applicable)</li>
              </ul>

              <h3>Security of Handling</h3>
              <ul>
                <li>All API communications use HTTPS/TLS encryption</li>
                <li>Authentication tokens are stored using Chrome's storage API</li>
                <li>We do not use your data for advertising, retargeting, or interest-based ads</li>
                <li>We do not sell your personal information</li>
              </ul>
            </section>

            <section>
              <h2>4. User Data Storage</h2>

              <h3>Where Your Data Is Stored</h3>

              <p><strong>On your device (Chrome Extension):</strong></p>
              <p>
                The FrameworkAI Chrome Extension stores the following data locally on your device using Chrome's <code>chrome.storage.local</code> API:
              </p>
              <ul>
                <li>Authentication token (JWT)</li>
                <li>User information (user ID, organization ID, role)</li>
                <li>Cached prompts from the Framework AI API</li>
                <li>Saved prompts (including those you create or save from ChatGPT)</li>
                <li>Extension settings and preferences</li>
                <li>Cache timestamps for sync management</li>
              </ul>
              <p>
                This data remains on your device and is not transmitted except when you sync with the Framework AI API (e.g., when logged in and syncing prompts).
              </p>

              <p><strong>On our servers:</strong></p>
              <p>When you use the Enterprise Prompt Library or log in through the extension:</p>
              <ul>
                <li>Your email address and account information are stored on our servers</li>
                <li>Your prompts (including those synced from the extension) are stored on our servers as part of your Framework AI account</li>
                <li>Product usage data may be stored in connection with your account</li>
              </ul>

              <h3>Storage Duration</h3>
              <ul>
                <li><strong>Locally (extension):</strong> Data remains in Chrome storage until you log out, clear extension data, or uninstall the extension</li>
                <li><strong>On our servers:</strong> We retain your account and prompt data for as long as your account is active or as needed to provide our services. You may request deletion at any time</li>
              </ul>
            </section>

            <section>
              <h2>5. User Data Sharing</h2>

              <h3>Who We Share Data With</h3>

              <p><strong>Framework AI API (api.frameworkai.com.au):</strong></p>
              <p>When you are logged in, we share the following with our API to provide the extension's functionality:</p>
              <ul>
                <li>Your authentication token (for API requests)</li>
                <li>User information (user ID, organization ID, role) for role-based prompt filtering</li>
                <li>Prompt data when you save a new prompt from the extension (prompt text, title, description, category)</li>
                <li>Requests to fetch your prompts, verify your token, and manage your account</li>
              </ul>

              <p><strong>Third parties:</strong></p>
              <ul>
                <li>We do <strong>not</strong> share your personal data with third parties for marketing, advertising, or analytics</li>
                <li>We do <strong>not</strong> sell your personal information</li>
                <li>We do <strong>not</strong> use your data for personalized or retargeted advertising</li>
              </ul>

              <p><strong>Legal and security:</strong></p>
              <p>
                We may disclose your information if required by law, to comply with legal process, to protect our rights or safety, or in connection with a merger, acquisition, or sale of assets (with appropriate notice and choice where required).
              </p>
            </section>

            <section>
              <h2>6. Data Security</h2>
              <p>
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction, including:
              </p>
              <ul>
                <li>HTTPS/TLS encryption for all data transmission</li>
                <li>Secure storage of authentication tokens via Chrome's storage API</li>
                <li>No storage of passwords—only tokens are retained</li>
                <li>Access controls and secure API design</li>
              </ul>
            </section>

            <section>
              <h2>7. Data Retention</h2>
              <ul>
                <li><strong>Account data:</strong> We retain your email address and account information for as long as your account is active or as needed to provide our services</li>
                <li><strong>Prompts:</strong> We retain your prompts for as long as they are part of your account. You may delete individual prompts or your entire account at any time</li>
                <li><strong>Extension local data:</strong> You can clear all extension data by logging out, using the extension's clear-data options, or uninstalling the extension</li>
              </ul>
            </section>

            <section>
              <h2>8. Your Rights</h2>
              <p>
                Under Australian privacy laws and in line with our practices, you have the right to:
              </p>
              <ul>
                <li><strong>Access:</strong> Request access to your personal information</li>
                <li><strong>Correction:</strong> Request correction of inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information and account</li>
                <li><strong>Withdraw consent:</strong> Withdraw consent where applicable</li>
                <li><strong>Control:</strong> Log out at any time to clear authentication data from the extension; use the extension's settings to control sync and data preferences</li>
              </ul>
              <p>
                To exercise these rights, please contact us (see Section 10).
              </p>
            </section>

            <section>
              <h2>9. Children's Privacy</h2>
              <p>
                Our services are not intended for users under the age of 13. We do not knowingly collect information from children under 13.
              </p>
            </section>

            <section>
              <h2>10. Contact Us</h2>
              <p>
                If you have any questions about this Privacy Policy, wish to exercise your rights, or have concerns about our data practices, please contact us:
              </p>
              <ul>
                <li><strong>Website:</strong> <a href="https://frameworkai.com.au" target="_blank" rel="noopener noreferrer" className="legal-link">https://frameworkai.com.au</a></li>
                <li><strong>Email:</strong> Through the contact options on our website</li>
              </ul>
            </section>

            <section>
              <h2>11. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this policy periodically.
              </p>
            </section>
          </div>

          <div className="legal-footer">
            <Link to="/terms-of-service" className="legal-link">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyScreen;
